import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import {
  KakarikoButtonLarge,
  KakarikoConfirmation,
  KakarikoForm,
  KakarikoSelect,
  KakarikoText,
  Validations,
} from 'library';
import { flatStateReducer, nestedStateReducer } from '../../../library/utils';
import { usaStates } from '../../../library/Inputs';
import './styles.scss';
import { IAddLocation, ILocationFormData } from './types';
import { RxProviderOptions } from '../../ManageLocations/utils';

export const initialFormData: ILocationFormData = {
  location: {
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    country: 'US',
    district: '',
    postalCode: '',
    externalRef: '',
  },
};

export const postalCodeErrorMessage =
  'US postal codes must have the format 00000 or 00000-0000.';

export const addLocationSuccessMessage = 'New location was added successfully';

export const emptyFieldErrorMessage = (field: string): string =>
  `${field} must not be empty.`;

export const LABELS = {
  name: 'Location Name',
  address: 'Location Address',
  address2: 'Location Address Line 2 (Optional)',
  city: 'City',
  state: 'State',
  zip: 'ZIP Code',
  rxProvider: 'RxProvider',
  pearConnectId: 'PearConnect ID',
};

export const AddLocation = (props: IAddLocation) => {
  const { handleSubmit, isDisabled } = props;
  const [formData, setFormData] = useReducer<
    React.Reducer<ILocationFormData, ILocationFormData>
  >(nestedStateReducer, { location: { ...initialFormData.location } });

  const [errors, setErrors] = useReducer<
    React.Reducer<booleanGenericObject, booleanGenericObject>
  >(flatStateReducer, {});
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const clearForm = () => {
    setFormData({
      location: { ...initialFormData.location },
    });
    setOpen(false);
  };

  const submitNewLocation = useCallback(async () => {
    if (!formData) return;
    setIsLoading(true);
    try {
      const { location } = formData;
      await WeaverApiClientInstance.createLocation(location);
      await handleSubmit();
      ErrorInterceptor.success(addLocationSuccessMessage, true);
      clearForm();
    } catch (e) {
      let errors = e;
      if (e.error) {
        errors = e.errors.map((m: any) => ({ message: m.message }));
      }

      if (e.response) {
        errors = { message: e.response.data.message };
      }

      ErrorInterceptor.error({ errors });
    } finally {
      setIsLoading(false);
    }
  }, [formData, handleSubmit]);

  const form = useMemo(
    () => (
      <KakarikoForm data={formData} setData={setFormData} setError={setErrors}>
        <KakarikoText
          errorMessage={emptyFieldErrorMessage('Name')}
          label={LABELS.name}
          name="location.name"
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText
          name="location.addressLine1"
          errorMessage={emptyFieldErrorMessage('Address')}
          label={LABELS.address}
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText name="location.addressLine2" label={LABELS.address2} />
        <KakarikoText
          errorMessage={emptyFieldErrorMessage('City')}
          label={LABELS.city}
          name="location.city"
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoSelect
          label={LABELS.state}
          name="location.district"
          options={usaStates.map((state) => state.abbreviation)}
          validation={[Validations.isNotEmpty]}
          width="half"
        />
        <KakarikoText
          errorMessage={postalCodeErrorMessage}
          label={LABELS.zip}
          name="location.postalCode"
          validation={[Validations.isValidPostalCode]}
          width="half"
        />
        <KakarikoSelect
          displayValue={(value) => value.name}
          value={RxProviderOptions[0].id}
          errorMessage={emptyFieldErrorMessage('Rx Provider')}
          label={LABELS.rxProvider}
          name="rxProvider"
          options={RxProviderOptions}
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText
          label={LABELS.pearConnectId}
          name="location.externalRef"
        />
      </KakarikoForm>
    ),
    [formData],
  );

  const confirmation = useMemo(
    () => (
      <>
        <div styleName="container">
          <div styleName="item1">Location Name:</div>
          <div styleName="item2">{formData.location.name}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Location Address:</div>
          <div styleName="item2">{formData.location.addressLine1}</div>
        </div>
        <div styleName="container">
          <div styleName="item1">Rx Provider:</div>
          <div styleName="item2">{RxProviderOptions[0].name}</div>
        </div>
      </>
    ),
    [formData],
  );

  return (
    <>
      <KakarikoButtonLarge
        buttonTheme="primary"
        disabled={isDisabled}
        handleClick={() => setOpen(true)}
      >
        ADD LOCATION
      </KakarikoButtonLarge>
      <KakarikoConfirmation
        confirmation={confirmation}
        confirmText="Create New Location"
        confirmTitle="Confirm Location"
        dialog={form}
        isLoading={isLoading}
        errors={errors}
        handleCancel={() => clearForm()}
        handleClose={() => clearForm()}
        handleSubmit={submitNewLocation}
        open={open}
        title="Add Location"
      />
    </>
  );
};
